import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  ArrowRight,
  Sparkles,
  Target,
  Heart,
  Globe,
  Lightbulb,
  Users,
  Shield,
  Zap,
  MessageSquare,
  TrendingUp,
  Award,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const values = [
  {
    icon: Heart,
    title: 'Customer First',
    description: 'Every feature we build starts with a real problem faced by Indian SMBs. Our customers are our compass.',
  },
  {
    icon: Lightbulb,
    title: 'Simplicity',
    description: 'Technology should empower, not overwhelm. We obsess over making powerful tools feel effortless.',
  },
  {
    icon: Shield,
    title: 'Trust & Transparency',
    description: 'Honest pricing, clear communication, and data security you can count on. No fine print, no surprises.',
  },
  {
    icon: Globe,
    title: 'India First',
    description: 'Built in India, for India. We understand the unique challenges and opportunities of Indian businesses.',
  },
  {
    icon: Zap,
    title: 'Speed of Execution',
    description: 'We ship fast, iterate faster, and never stop improving. Our users get new features every sprint.',
  },
  {
    icon: Users,
    title: 'Community Driven',
    description: 'Our best ideas come from our users. We build in the open and listen to every piece of feedback.',
  },
];

const milestones = [
  { year: '2024 Q1', title: 'The Spark', description: 'Identified the gap — Indian SMBs needed an easy, affordable WhatsApp AI solution without Business API complexity.' },
  { year: '2024 Q2', title: 'First Prototype', description: 'Built the first working prototype. Tested with 10 local businesses in Bengaluru. Validated product-market fit.' },
  { year: '2024 Q3', title: 'Public Beta', description: 'Launched public beta with AI chat agent, knowledge base, and lead capture. Onboarded 100+ businesses.' },
  { year: '2025 Q1', title: 'Official Launch', description: 'Full platform launch with pricing tiers, analytics, and support automation. Growing fast across India.' },
];

const stats = [
  { value: '500+', label: 'Businesses Onboarded' },
  { value: '50K+', label: 'Conversations Handled' },
  { value: '<2s', label: 'Avg. Response Time' },
  { value: '95%', label: 'Customer Satisfaction' },
];

const team = [
  { name: 'Arjun Mehta', role: 'Co-Founder & CEO', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face' },
  { name: 'Priya Nair', role: 'Co-Founder & CTO', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&crop=face' },
  { name: 'Karthik Iyer', role: 'Head of Product', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face' },
  { name: 'Sneha Sharma', role: 'Head of Growth', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face' },
];

function StatsSection() {
  const [ref, isVisible] = useScrollAnimation(0.2);
  return (
    <section ref={ref} className="py-16 border-t border-b border-border/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center"
            >
              <p className="font-heading text-3xl lg:text-4xl font-bold gradient-text-primary mb-1">
                {stat.value}
              </p>
              <p className="text-sm text-muted-foreground font-body">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <section ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-accent/[0.03] blur-[100px]" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="accent" className="mb-4">Our Values</Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight"
          >
            What Drives Us Every Day
          </motion.h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 25 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.08 }}
              className="glass-card rounded-2xl p-7 hover:border-primary/15 transition-colors duration-500"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <v.icon size={18} className="text-primary" />
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground mb-2">{v.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-body">{v.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function JourneySection() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <section ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute top-1/3 left-0 w-[300px] h-[300px] rounded-full bg-primary/[0.03] blur-[80px]" />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="premium" className="mb-4">Our Journey</Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight"
          >
            From Idea to Impact
          </motion.h2>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 lg:left-1/2 lg:-translate-x-px top-0 bottom-0 w-px bg-border" />

          <div className="space-y-10">
            {milestones.map((m, i) => (
              <motion.div
                key={m.year}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                animate={isVisible ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}
                className={`relative flex items-start gap-6 lg:gap-0 ${
                  i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Dot */}
                <div className="absolute left-6 lg:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary ring-4 ring-background z-10 mt-1.5" />

                {/* Content card */}
                <div className={`ml-14 lg:ml-0 lg:w-[calc(50%-2rem)] ${
                  i % 2 === 0 ? 'lg:pr-0 lg:mr-auto' : 'lg:pl-0 lg:ml-auto'
                }`}>
                  <div className="glass-card rounded-xl p-6">
                    <Badge variant="glass" className="text-[10px] mb-3">{m.year}</Badge>
                    <h3 className="font-heading text-base font-semibold text-foreground mb-1">{m.title}</h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">{m.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <section ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full bg-primary/[0.04] blur-[120px]" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="premium" className="mb-4">Our Team</Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight"
          >
            The People Behind{' '}
            <span className="gradient-text-primary">Agentick</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base text-muted-foreground font-body leading-relaxed"
          >
            A passionate team of builders, dreamers, and problem-solvers obsessed with making AI accessible for every Indian business.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 25 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-5 text-center hover:border-primary/15 transition-colors duration-500">
                <div className="relative w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden ring-2 ring-border group-hover:ring-primary/30 transition-all duration-500">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground mb-0.5">{member.name}</h3>
                <p className="text-xs text-muted-foreground font-body">{member.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-primary/[0.06] blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-accent/[0.04] blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(hsl(210 20% 95% / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(210 20% 95% / 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
              <Badge variant="premium" className="mb-6">
                <Sparkles size={12} className="mr-1" />
                Our Story
              </Badge>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight"
            >
              Making AI Conversations{' '}
              <span className="gradient-text-primary">Accessible</span>{' '}
              for Every Indian Business
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed max-w-2xl mx-auto mb-10"
            >
              Agentick was born from a simple observation: Indian SMBs deserve the same powerful AI tools that enterprises use — without the complexity, cost, or technical overhead.
            </motion.p>

            {/* Mission & Vision cards */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto"
            >
              <div className="glass-card rounded-2xl p-7 text-left glow-border-primary">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                    <Target size={16} className="text-primary" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground">Our Mission</h3>
                </div>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  Empower 10 million Indian SMBs with AI-powered WhatsApp automation that’s as simple as sending a message.
                </p>
              </div>
              <div className="glass-card rounded-2xl p-7 text-left">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-accent/15 flex items-center justify-center">
                    <TrendingUp size={16} className="text-accent" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground">Our Vision</h3>
                </div>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  A world where every business — no matter how small — can deliver world-class customer experiences through AI.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <StatsSection />
      <ValuesSection />
      <JourneySection />
      <TeamSection />

      {/* CTA */}
      <section className="py-24 lg:py-32 relative">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[350px] rounded-full bg-primary/[0.05] blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-3xl p-10 lg:p-14 text-center glow-border-primary"
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-5">
              <Award size={22} className="text-primary" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3 leading-tight">
              Join Our Journey
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed max-w-md mx-auto mb-8">
              Whether you’re a business looking for AI automation or a talented individual wanting to make an impact — we’d love to connect.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="premium" size="lg" asChild>
                <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">
                  Start Free Trial <ArrowRight size={16} />
                </a>
              </Button>
              <Button variant="glass" size="lg" asChild>
                <a href="/careers">We’re Hiring</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
